import "./ToggleSwitch.scss";
import Switch from "@mui/material/Switch";
import ClearIcon from "@mui/icons-material/Clear";

/**
 * values: [offValue, onValue]
 * - default is ["N","Y"] to preserve existing behavior.
 *
 * normalizeValue:
 * - optional function to map incoming value (null/undefined/"" etc.)
 *   into one of the values, e.g. (v)=> v ?? "N"
 */
export const ToggleSwitch = ({
  id,
  value,
  onChange,
  label,
  labelPosition = "start",
  labelWidth = "150px",
  labelStyle = {},
  delete_yn = false,
  disabled = false,
  border = "N",
  onClickClear,
  values = ["N", "Y"],
  normalizeValue,
}) => {
  const offValue = values?.[0];
  const onValue = values?.[1];

  const currentValue = normalizeValue ? normalizeValue(value) : value;
  const isOn = currentValue === onValue;

  const handleToggle = (e) => {
    if (e) {
      e.stopPropagation();
      if (disabled) return;
      const next = isOn ? offValue : onValue;
      onChange(next);
    }
  };

  const handleClearClick = (event) => {
    event.stopPropagation();
    if (onClickClear) onClickClear(id);
  };

  return (
    <div
      className={`toggle-switch ${border === "Y" ? "border" : ""} ${isOn ? "active" : ""
        } ${disabled ? "disabled" : ""}`}
      onClick={handleToggle}
      role="switch"
      aria-checked={isOn}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          handleToggle();
        }
      }}
    >
      {labelPosition === "start" && (
        <div className="labels" style={{ width: labelWidth }}>
          <div className="label" style={labelStyle}>
            {label}
          </div>
        </div>
      )}

      <div className="switch" />

      {labelPosition === "end" && (
        <div className="labels" style={{ width: labelWidth }}>
          <div className="label" style={labelStyle}>
            {label}
          </div>
        </div>
      )}

      {delete_yn && currentValue && !disabled && (
        <ClearIcon onClick={handleClearClick} />
      )}
    </div>
  );
};

export const ToggleSwitchRadio = ({
  id,
  value,
  onChange,
  label,
  labelPosition = "start",
  labelWidth = "150px",
  labelStyle = {},
  delete_yn = false,
  disabled = false,
  border = "N",
  onClickClear,

  // NEW
  values = ["N", "Y"],
  normalizeValue,
}) => {
  const offValue = values?.[0];
  const onValue = values?.[1];

  const currentValue = normalizeValue ? normalizeValue(value) : value;
  const isOn = currentValue === onValue;

  const handleToggle = () => {
    if (disabled) return;
    const next = isOn ? offValue : onValue;
    onChange(next);
  };

  const handleClearClick = (event) => {
    event.stopPropagation();
    if (onClickClear) onClickClear(id);
  };

  return (
    <div
      className={`toggle-switch ${border === "Y" ? "border" : ""} ${isOn ? "active" : ""
        } ${disabled ? "disabled" : ""}`}
      onClick={handleToggle}
      role="switch"
      aria-checked={isOn}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleToggle();
        }
      }}
    >
      {labelPosition === "start" && (
        <div className="labels" style={{ width: labelWidth }}>
          <div className="label" style={labelStyle}>
            {label}
          </div>
        </div>
      )}

      <div className="switch" />

      {labelPosition === "end" && (
        <div className="labels" style={{ width: labelWidth }}>
          <div className="label" style={labelStyle}>
            {label}
          </div>
        </div>
      )}

      {delete_yn && isOn && <ClearIcon onClick={handleClearClick} />}
    </div>
  );
};

// Keep this as-is (it’s already boolean-based, but you can also add values if you want)
export const ToggleSwitchInLoop = ({ id, value, onChange }) => {
  const handleToggleChange = (event) => {
    const newValue = event.target.checked ? "Y" : "N";
    onChange(newValue);
  };

  return (
    <Switch
      checked={value === "Y"}
      onChange={handleToggleChange}
      inputProps={{ "aria-label": "Toggle switch" }}
      sx={{
        "& .MuiSwitch-thumb": {
          width: 24,
          height: 24,
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
          backgroundColor: "#fff",
        },
        "& .MuiSwitch-track": {
          borderRadius: 26 / 2,
          backgroundColor: value === "Y" ? "#4caf50" : "#ccc",
        },
      }}
    />
  );
};
